import dayjs from 'dayjs';
import dayOfYear from 'dayjs/plugin/dayOfYear';
import isLeapYear from 'dayjs/plugin/isLeapYear';
import { getSign } from 'horoscope';

dayjs.extend(dayOfYear);
dayjs.extend(isLeapYear);

/**
 * Calculate age
 */
export const getAge = dateOfBirth => {
  let then = dayjs(dateOfBirth);
  const now = dayjs(); // defaults to current date

  // extract years, months, weeks, and days
  const years = now.diff(then, 'years');
  then = then.add(years, 'years');
  const months = now.diff(then, 'months');
  then = then.add(months, 'months');
  const weeks = now.diff(then, 'weeks');
  then = then.add(weeks, 'weeks');
  const days = now.diff(then, 'days');

  return `You are ${formatAge(years, months, weeks, days)} old`;
};

/**
 * Zodiac sign finder
 */
export const getZodiacSign = dateOfBirth => {
  const then = dayjs(dateOfBirth);

  const birthDay = then.date();
  // month should be numbers 1-12. "dayjs" months starts from 0
  const birthMonth = then.month() + 1;
  const sign = getSign({ month: birthMonth, day: birthDay });
  return sign;
};

/**
 * Age difference calculator
 */
export const getAgeDifference = (firstDate, secondDate) => {
  let dateOne = dayjs(firstDate);
  let dateTwo = dayjs(secondDate);

  // person two older than person one
  const daysDiff = dateTwo.diff(dateOne);

  // person one and person two have the same age
  if (daysDiff === 0) {
    return 'Both persons have the same age';
  }

  if (daysDiff > 0) {
    const years = dateTwo.diff(dateOne, 'years');
    dateOne = dateOne.add(years, 'years');
    const months = dateTwo.diff(dateOne, 'months');
    dateOne = dateOne.add(months, 'months');
    const weeks = dateTwo.diff(dateOne, 'weeks');
    dateOne = dateOne.add(weeks, 'weeks');
    const days = dateTwo.diff(dateOne, 'days');

    const ageDiff = formatAge(years, months, weeks, days);
    return `First Person is <b>${ageDiff}</b> older than the second person`;
  }

  // person one is older than person two
  if (daysDiff < 0) {
    const years = dateOne.diff(dateTwo, 'years');
    dateTwo = dateTwo.add(years, 'years');
    const months = dateOne.diff(dateTwo, 'months');
    dateTwo = dateTwo.add(months, 'months');
    const weeks = dateOne.diff(dateTwo, 'weeks');
    dateTwo = dateTwo.add(weeks, 'weeks');
    const days = dateOne.diff(dateTwo, 'days');

    const ageDiff = formatAge(years, months, weeks, days);
    return `Second person is ${ageDiff} older than first person`;
  }
};

/**
 * Days left calculator
 */
export const getDaysLeft = date => {
  const future = dayjs(date);
  const current = dayjs();

  const diff = future.diff(current, 'days', true);

  return `There are <b>
    ${Math.ceil(diff)} day${diff === 1 ? '' : 's'}
  </b> left until <b>${future.format('YYYY/DD/MM')}</b>`;
};

/**
 * Day of the week calculator
 */
export const getDayOfTheWeek = date => {
  const day = dayjs(date).format('dddd');
  return `The day on <b>${dayjs(date).format('YYYY/DD/MM')}</b> was a <b>${day}</b>`;
};

/**
 * Age on other planets
 */
export const getAgeOnOtherPlanets = dateOBirth => {
  /**
   * PLANET	      ROTATION PERIOD (day)	    ORBIT PERIOD (year)
   *
   * Mercury	    58.646      days	        87.97      days
   * Venus	      243.018     days	        224.70     days
   * Mars	        1.026       days	        1.8808476  years
   * Jupiter	    0.41354     days	        11.862615  years
   * Saturn	      0.444       days	        29.447498  years
   * Uranus	      0.718       days	        84.016846  years
   * Neptune	    0.671       days	        164.79132  years
   * Pluto      	6.387       days	        247.92065  years
   */

  const then = dayjs(dateOBirth);
  const now = dayjs();

  const earthDays = now.diff(then, 'days');
  const earthYears = now.diff(then, 'years');

  return [
    {
      name: 'Earth',
      years: formatPlanetYears(earthYears, 1)
    },
    {
      name: 'Mercury',
      years: formatPlanetYears(earthDays, 87.97)
    },
    {
      name: 'Venus',
      years: formatPlanetYears(earthDays, 224.7)
    },
    {
      name: 'Mars',
      years: formatPlanetYears(earthYears, 1.8808476)
    },
    {
      name: 'Jupiter',
      years: formatPlanetYears(earthYears, 11.862615)
    },
    {
      name: 'Saturn',
      years: formatPlanetYears(earthYears, 29.447498)
    },
    {
      name: 'Uranus',
      years: formatPlanetYears(earthYears, 84.016846)
    },
    {
      name: 'Neptune',
      years: formatPlanetYears(earthYears, 164.79132)
    },
    {
      name: 'Pluto',
      years: formatPlanetYears(earthYears, 247.92065)
    }
  ];
};

/**
 * Calculate next birthday
 */
export const getDaysLeftToNextBirthDay = dateOfBirth => {
  //  const next =
  const nextBirthDay = getNextBirthDay(dateOfBirth);
  const today = dayjs();
  const daysLeft = nextBirthDay.diff(today, 'days');
  const dayOfTheWeek = nextBirthDay.format('dddd');

  if (daysLeft > 1) {
    return `Your next birthday is in <b>${daysLeft} days</b> on a <b>${dayOfTheWeek}</b>`;
  } else if (daysLeft === 1) {
    return 'Your next birthday is <b>tomorrow</b>';
  } else if (daysLeft === 0) {
    return 'Today is your birthday. Happy birthday!';
  }
};

/**
 * Calculate how many days you lived
 */
export const getDaysYouLived = dateOfBirth => {
  const then = dayjs(dateOfBirth);
  const now = dayjs();

  const diff = now.diff(then, 'day');
  if (diff === 0) {
    return 'You have lived less than 1 second';
  }
  return `You have lived <b>${diff} day${diff !== 1 ? 's' : ''}</b>`;
};

/**
 * Calculate the days difference between two dates
 */
export const getDaysDifference = (dateOne, dateTwo) => {
  const firstDate = dayjs(dateOne);
  const secondDate = dayjs(dateTwo);

  const diff = Math.abs(secondDate.diff(firstDate, 'day'));

  const firstDateFormatted = firstDate.format('YYYY/DD/MM');
  const secondDateFormatted = secondDate.format('YYYY/DD/MM');

  if (diff === 1) {
    return `There is <b>1 day</b> between <b>${firstDateFormatted}</b> and <b>${secondDateFormatted}</b>`;
  }

  return `There are <b>${diff}</b> days between <b>${firstDateFormatted}</b> and <b>${secondDateFormatted}</b>`;
};

/**
 * Helper functions
 */
const getNextBirthDay = dateOfBirth => {
  const then = dayjs(dateOfBirth);
  const now = dayjs();

  let dayOfBirth = then.dayOfYear();
  let today = now.dayOfYear();

  if (then.isLeapYear() && !now.isLeapYear() && dayOfBirth >= 31 + 29) {
    dayOfBirth--;
  }
  if (now.isLeapYear() && !then.isLeapYear() && today >= 31 + 29) {
    today--;
  }

  const daysDiff = dayOfBirth - today;

  let nextBirthday;
  if (daysDiff > 0) {
    // this year's birthday hasn't passed yet
    nextBirthday = then.year(now.year());
  } else if (daysDiff === 0) {
    // currentDate is the birthday
    nextBirthday = then;
  } else {
    // this year's birthday has passed. Next birthday is in the next year
    nextBirthday = then.year(now.year() + 1);
  }
  return nextBirthday;
};
const formatAge = (years, months, weeks, days) => {
  // "years", "months", and "days" can not be "<0" thanks to the "validateDates()" method
  let age = '';
  if (years > 1) {
    if (months > 1) {
      if (weeks > 1) {
        if (days > 1) {
          age = `${years} years, ${months} months, ${weeks} weeks, and ${days} days`;
        } else if (days === 1) {
          age = `${years} years, ${months} months, ${weeks} weeks, and 1 day`;
        } else if (days === 0) {
          age = `${years} years, ${months} months, and ${weeks} weeks`;
        }
      } else if (weeks === 1) {
        if (days > 1) {
          age = `${years} years, ${months} months, 1 week, and ${days} days`;
        } else if (days === 1) {
          age = `${years} years, ${months} months, 1 week, and 1 day`;
        } else if (days === 0) {
          age = `${years} years, ${months} months, and 1 week`;
        }
      } else if (weeks === 0) {
        if (days > 1) {
          age = `${years} years, ${months} months, and ${days} days`;
        } else if (days === 1) {
          age = `${years} years, ${months} months, and 1 day`;
        } else if (days === 0) {
          age = `${years} years and ${months} months`;
        }
      }
    } else if (months === 1) {
      if (weeks > 1) {
        if (days > 1) {
          age = `${years} years, 1 month, ${weeks} weeks, and ${days} days`;
        } else if (days === 1) {
          age = `${years} years, 1 month, ${weeks} weeks, and 1 day`;
        } else if (days === 0) {
          age = `${years} years, 1 month, and ${weeks} weeks`;
        }
      } else if (weeks === 1) {
        if (days > 1) {
          age = `${years} years, 1 month, 1 week, and ${days} days`;
        } else if (days === 1) {
          age = `${years} years, 1 month, 1 week, and 1 day`;
        } else if (days === 0) {
          age = `${years} years, 1 month, and 1 week`;
        }
      } else if (weeks === 0) {
        if (days > 1) {
          age = `${years} years, 1 month, and ${days} days`;
        } else if (days === 1) {
          age = `${years} years, 1 month, and 1 day`;
        } else if (days === 0) {
          age = `${years} years and 1 month`;
        }
      }
    } else if (months === 0) {
      if (weeks > 1) {
        if (days > 1) {
          age = `${years} years, ${weeks} weeks, and ${days} days`;
        } else if (days === 1) {
          age = `${years} years, ${weeks} weeks, and 1 day`;
        } else if (days === 0) {
          age = `${years} years and ${weeks} weeks`;
        }
      } else if (weeks === 1) {
        if (days > 1) {
          age = `${years} years, 1 week, and ${days} days`;
        } else if (days === 1) {
          age = `${years} years, 1 week, and 1 day`;
        } else if (days === 0) {
          age = `${years} years and 1 week`;
        }
      } else if (weeks === 0) {
        if (days > 1) {
          age = `${years} years and ${days} days`;
        } else if (days === 1) {
          age = `${years} years and 1 day`;
        } else if (days === 0) {
          age = `${years} years`;
        }
      }
    }
  } else if (years === 1) {
    if (months > 1) {
      if (weeks > 1) {
        if (days > 1) {
          age = `1 year, ${months} months, ${weeks} weeks, and ${days} days`;
        } else if (days === 1) {
          age = `1 year, ${months} months, ${weeks} weeks, and 1 day`;
        } else if (days === 0) {
          age = `1 year, ${months} months, and ${weeks} weeks`;
        }
      } else if (weeks === 1) {
        if (days > 1) {
          age = `1 year, ${months} months, 1 week, and ${days} days`;
        } else if (days === 1) {
          age = `1 year, ${months} months, 1 week, and 1 day`;
        } else if (days === 0) {
          age = `1 year, ${months} months, and 1 week`;
        }
      } else if (weeks === 0) {
        if (days > 1) {
          age = `1 year, ${months} months, and ${days} days`;
        } else if (days === 1) {
          age = `1 year, ${months} months, and 1 day`;
        } else if (days === 0) {
          age = `1 year and ${months} months`;
        }
      }
    } else if (months === 1) {
      if (weeks > 1) {
        if (days > 1) {
          age = `1 year, 1 month, ${weeks} weeks, and ${days} days`;
        } else if (days === 1) {
          age = `1 year, 1 month, ${weeks} weeks, and 1 day`;
        } else if (days === 0) {
          age = `1 year, 1 month, and ${weeks} weeks`;
        }
      } else if (weeks === 1) {
        if (days > 1) {
          age = `1 year, 1 month, 1 week, and ${days} days`;
        } else if (days === 1) {
          age = `1 year, 1 month, 1 week, and 1 day`;
        } else if (days === 0) {
          age = `1 year, 1 month, and 1 week`;
        }
      } else if (weeks === 0) {
        if (days > 1) {
          age = `1 year, 1 month, and ${days} days`;
        } else if (days === 1) {
          age = `1 year, 1 month, and 1 day`;
        } else if (days === 0) {
          age = `1 year and 1 month`;
        }
      }
    } else if (months === 0) {
      if (weeks > 1) {
        if (days > 1) {
          age = `1 year, ${weeks} weeks, and ${days} days`;
        } else if (days === 1) {
          age = `1 year, ${weeks} weeks, and 1 day`;
        } else if (days === 0) {
          age = `1 year and ${weeks} weeks`;
        }
      } else if (weeks === 1) {
        if (days > 1) {
          age = `1 year, 1 week, and ${days} days`;
        } else if (days === 1) {
          age = `1 year, 1 week, and 1 day`;
        } else if (days === 0) {
          age = `1 year and 1 week`;
        }
      } else if (weeks === 0) {
        if (days > 1) {
          age = `1 year and ${days} days`;
        } else if (days === 1) {
          age = `1 year and 1 day`;
        } else if (days === 0) {
          age = `1 year`;
        }
      }
    }
  } else if (years === 0) {
    if (months > 1) {
      if (weeks > 1) {
        if (days > 1) {
          age = `${months} months, ${weeks} weeks, and ${days} days`;
        } else if (days === 1) {
          age = `${months} months, ${weeks} weeks, and 1 day`;
        } else if (days === 0) {
          age = `${months} months and ${weeks} weeks`;
        }
      } else if (weeks === 1) {
        if (days > 1) {
          age = `${months} months, 1 week, and ${days} days`;
        } else if (days === 1) {
          age = `${months} months, 1 week, and 1 day`;
        } else if (days === 0) {
          age = `${months} months and 1 week`;
        }
      } else if (weeks === 0) {
        if (days > 1) {
          age = `${months} months and ${days} days`;
        } else if (days === 1) {
          age = `${months} months and 1 day`;
        } else if (days === 0) {
          age = `${months} months`;
        }
      }
    } else if (months === 1) {
      if (weeks > 1) {
        if (days > 1) {
          age = `1 month, ${weeks} weeks, and ${days} days`;
        } else if (days === 1) {
          age = `1 month, ${weeks} weeks, and 1 day`;
        } else if (days === 0) {
          age = `1 month and ${weeks} weeks`;
        }
      } else if (weeks === 1) {
        if (days > 1) {
          age = `1 month, 1 week, and ${days} days`;
        } else if (days === 1) {
          age = '1 month, 1 week, and 1 day';
        } else if (days === 0) {
          age = '1 month and 1 week';
        }
      } else if (weeks === 0) {
        if (days > 1) {
          age = `1 month and ${days} days`;
        } else if (days === 1) {
          age = '1 month and 1 day';
        } else if (days === 0) {
          age = '1 month';
        }
      }
    } else if (months === 0) {
      if (weeks > 1) {
        if (days > 1) {
          age = `${weeks} weeks and ${days} days`;
        } else if (days === 1) {
          age = `${weeks} weeks and 1 day`;
        } else if (days === 0) {
          age = `${weeks} weeks`;
        }
      } else if (weeks === 1) {
        if (days > 1) {
          age = `1 week and ${days} days`;
        } else if (days === 1) {
          age = '1 week and 1 day';
        } else if (days === 0) {
          age = '1 week';
        }
      } else if (weeks === 0) {
        if (days > 1) {
          age = `${days} days`;
        } else if (days === 1) {
          age = '1 day';
        } else if (days === 0) {
          age = 'less than 1 second';
        }
      }
    }
  }
  return age;
};
const formatPlanetYears = (earthYears, coefficient) => {
  if (coefficient === 1) {
    return earthYears !== 1 ? `${earthYears} years` : '1 year';
  }
  const planetYears = (earthYears / coefficient).toFixed(2);
  return planetYears !== 1 ? `${planetYears} years` : '1 year';
};
